import { ProductAttribute } from "@wttb/product-configurator";
import cn from "clsx";
import { useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import { useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";

import { useStoreProduct } from "@/features/product/contexts/product-context";
import { formatPrice } from "@/features/product/hooks/use-price";
import { TableHeader, TableRow, TableRowWithControlText } from "./components";
import {
  DEFAULT_LIST_LENGTH,
  getPricesDifference,
  MAX_LIST_LENGTH,
  prepareCollapsedPricesForView,
} from "./utils";

import "./styles/index.css";

export type PriceItem = {
  id: string;
  value: number;
  unitPrice: string;
  totalPrice: string;
  discountTotalPrice: string;
  priceIncrease: string | null;
};

export const QuantityTable: React.FC<{
  attribute: ProductAttribute;
  min: number;
  max: number;
  prices: PriceItem[];
  className?: string;
  setQuantity: (id: string, value: number) => void;
}> = ({ attribute, max, prices, setQuantity }) => {
  const t = useTranslations("custom-attributes-forms");
  const { custom } = attribute;
  const { config, pricingResult } = useStoreProduct();
  const { resetField, watch } = useFormContext<{ quantity: number }>();
  const customQuantity = watch("quantity");

  const router = useRouter();

  const maxQuantityValue = useMemo<PriceItem | null>(() => {
    if (!custom || customQuantity !== max) return null;

    const discountDiff = getPricesDifference(
      pricingResult?.salePrice.total,
      pricingResult?.listPrice.total,
    );
    return {
      id: "max-quantity-value",
      value: customQuantity,
      unitPrice: formatPrice(pricingResult?.salePrice?.unit, "en", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 3,
      }),
      totalPrice: formatPrice(pricingResult?.listPrice?.total),
      discountTotalPrice: discountDiff ? formatPrice(pricingResult?.salePrice.total) : "",
      priceIncrease: null,
    };
  }, [pricingResult, custom, max, customQuantity]);

  const alwaysVisibleValues = useMemo(
    () => prepareCollapsedPricesForView(attribute, prices, config!["quantity"] as number),
    [attribute, prices, config?.["quantity"]],
  );

  const [listSize, setListSize] = useState<number>(
    alwaysVisibleValues.length || DEFAULT_LIST_LENGTH,
  );
  const isFullList = !!maxQuantityValue || listSize >= prices.length;

  const quantitiesToShow = useMemo(() => {
    if (maxQuantityValue) return [...prices, maxQuantityValue];
    if (isFullList) return [...prices];
    return alwaysVisibleValues.length ? alwaysVisibleValues : prices.slice(0, listSize);
  }, [prices, maxQuantityValue, alwaysVisibleValues, isFullList]);

  const isOverflowedList = useMemo(
    () => isFullList && quantitiesToShow.length > MAX_LIST_LENGTH,
    [quantitiesToShow, isFullList],
  );

  const tableRows = useMemo(() => {
    const handleRowClick = (id: string, value: number) => {
      resetField("quantity", { keepDirty: false });
      setQuantity(id, value);
    };
    const items = quantitiesToShow.map((price, ind) => {
      const isSelected =
        (!customQuantity && price.value === config!["quantity"]) ||
        maxQuantityValue?.value === price?.value;
      return (
        <TableRow
          key={price.id}
          price={price}
          setQuantity={(value) => handleRowClick(price.id, value)}
          isSelected={isSelected}
          className={cn({
            "rounded-t-lg border-t-0": ind === 0,
            "rounded-b-lg": ind === quantitiesToShow.length - 1 && !isOverflowedList,
          })}
        />
      );
    });

    if (!isFullList) {
      items.splice(
        Math.round(items.length / 2),
        0,
        <TableRowWithControlText
          key="show-all-quantities"
          text={t("button-show-all-quantities")}
          onClick={() => setListSize(prices.length)}
          className="!text-berry font-medium underline"
        />,
      );
    }

    return items;
  }, [prices, listSize, config, setQuantity, customQuantity, maxQuantityValue, isFullList]);

  useEffect(() => {
    if (!customQuantity) return;
    // If custom quantity exist and the value is in the collapsed list
    // show full rows
    const valueIndex = quantitiesToShow.findIndex((q) => q.value === customQuantity);
    if (!isFullList && valueIndex !== -1) {
      setListSize(prices.length);
    }
  }, [customQuantity, quantitiesToShow, isOverflowedList, isFullList]);

  return (
    <div className="flex flex-col">
      <TableHeader
        quantityText={t("text-fixed-quantity")}
        hideLastColumn={custom}
        className={isOverflowedList ? "scroll-header-shift" : ""}
      />

      <div className="grid-rows border-grey-60 relative grid overflow-hidden rounded-lg border">
        <div
          className={cn("grid-rows grid", {
            // 10 rows height and a half
            "max-h-[504px] overflow-y-scroll quantity-table-rows shadow": isOverflowedList,
          })}
        >
          {tableRows}
        </div>

        {isFullList && (
          <TableRowWithControlText
            text={t("button-need-higher")}
            onClick={() => router.push("/bespoke")}
          />
        )}
      </div>
    </div>
  );
};
